/**
 * Universal search — one place that finds anything in the workspace: records,
 * users, and the app's own pages. The ⌘K palette asks the server, the server
 * asks this.
 *
 * Nothing here is an index that can go stale: every search reads the records
 * as they are right now, so something created a second ago is found. On the
 * sample (mock mode, the sandbox) the records are read whole and ranked here;
 * on the real records the database finds a few of each kind first — a record
 * number exactly, else `ILIKE` on the trigram-indexed names (DATA-MODEL-V1 §9)
 * — and the same `hitsFor…` and `rankHits` turn them into results.
 *
 * Adding a new kind of record: add a kind below, a `hitsFor…` function, and
 * the repository to `searchedRepositories`. search.test.ts (in features/system)
 * fails until every repository is searched or excluded with a reason.
 */
import fuzzysort from 'fuzzysort'
import { z } from 'zod'
import { accessForPath, can, type Role, roleLabels } from '../access/index.ts'
import { type Item, statusLabels } from '../items/index.ts'
import type { JobRow } from '../jobs/job-row.ts'
import type { FeatureEntry } from '../system/types.ts'
import type { WorkspaceUser } from '../users/index.ts'

/** What a result is. The palette shows an icon and this label for each. */
export const searchKinds = {
  item: 'Project item',
  job: 'Job',
  client: 'Client',
  location: 'Location',
  cleaner: 'Cleaner',
  invoice: 'Invoice',
  order: 'Order',
  user: 'User',
  invitation: 'Invitation',
  page: 'Page',
} as const
export type SearchKind = keyof typeof searchKinds

/**
 * Each repository, and how it is searched — or why it isn't. Keyed by the
 * repository name in src/data/repositories/index.ts.
 */
export const searchedRepositories: Record<string, SearchKind[] | { notSearched: string }> = {
  items: ['item'],
  jobs: ['job'],
  clients: ['client', 'location'],
  // A person is found as the client they are on, or as the cleaner they are.
  contacts: ['client', 'cleaner'],
  cleaners: ['cleaner'],
  invoices: ['invoice'],
  products: {
    notSearched: 'a short catalog, read on Inventory',
  },
  orders: ['order'],
  inspections: {
    notSearched: 'each is one text to the inspector, read on Inspections',
  },
  communications: {
    notSearched:
      'texts and emails are read in the Activity log and Messages of the record they are about',
  },
  businessRules: {
    notSearched: 'one settings record, reached from Settings',
  },
  users: ['user', 'invitation'],
  assistant: {
    notSearched: 'conversations are private to their owner — never searchable by anyone else',
  },
  history: {
    notSearched: 'the activity log is read from each record’s History, not searched on its own',
  },
  preferences: {
    notSearched:
      'personal UI settings, like sidebar width — private to their owner and not the kind of thing anyone searches for',
  },
  recordHistory: {
    notSearched: 'a record’s past values are read from its History, not searched on their own',
  },
  dataDictionary: {
    notSearched:
      'words about fields and codes, searched on Settings → Data dictionary itself (which is in ⌘K) — not records anyone looks up',
  },
  autoJoinDomains: {
    notSearched:
      'a few Admin-only settings on Settings → Users, not records — the Users page is where they are found',
  },
  mcpAccess: {
    notSearched:
      'credentials, approved AI apps and a usage log (docs/MCP.md) — listed on Account and Settings → MCP access, never searchable',
  },
  webhooks: {
    notSearched:
      'an Admin setting with a handful of rows, reached from Settings — and its deliveries are a log, not records anyone looks up by name',
  },
  alertFlags: {
    notSearched:
      'the no-data alert’s flag history — a log read on each webhook’s own page, not records anyone looks up',
  },
}

export const searchHitSchema = z.object({
  kind: z.enum(Object.keys(searchKinds) as [SearchKind, ...SearchKind[]]),
  id: z.string(),
  title: z.string(),
  /** Where it lives, e.g. "Projects · In progress" — the result's parent. */
  parent: z.string(),
  /** Where opening it goes. Only results the searcher may open are returned. */
  url: z.string(),
})
export type SearchHit = z.infer<typeof searchHitSchema>

export const searchInputSchema = z.object({ q: z.string().trim().min(1).max(100) })

/** Case- and accent-insensitive. */
const fold = (text: string) => text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** Below this, a fuzzy match is more likely noise than the thing you meant. */
const FUZZY_THRESHOLD = 0.2
/** Fuzzy matching only from this many letters — "oe" would match half the app. */
const FUZZY_MIN_LENGTH = 3

/**
 * How well `fields` match the query. Two tiers, so the obvious answer always
 * wins and typos still find something (ADR fuzzysort):
 *
 *   3 — the first field starts with what you typed
 *   2 — a word in it starts with the first word you typed
 *   1 — every word you typed appears somewhere, in any field
 *   0 < n < 1 — a forgiving match: a typo, a missing letter, words out of
 *               order ("hendrson", "invoce", "dental oak"). From 3 letters.
 *   0 — no match
 */
export function score(query: string, fields: readonly string[]): number {
  const words = fold(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) return 0
  const haystack = fields.map(fold)
  const all = haystack.join(' ')
  if (words.every((word) => all.includes(word))) {
    const first = haystack[0] ?? ''
    const phrase = words.join(' ')
    if (first.startsWith(phrase)) return 3
    if (first.split(/[\s\-_.@]+/).some((part) => part.startsWith(words[0] ?? ''))) return 2
    return 1
  }
  const phrase = words.join(' ')
  if (phrase.replace(/\s/g, '').length < FUZZY_MIN_LENGTH) return 0
  const [best] = fuzzysort.go(phrase, haystack, { threshold: FUZZY_THRESHOLD, limit: 1 })
  // Kept strictly below 1, so every exact match outranks every fuzzy one.
  return best ? Math.min(best.score, 0.99) : 0
}

/** A possible result, plus the text it is matched on (which isn't sent back). */
export type Candidate = SearchHit & { fields: readonly string[] }

export function hitsForItems(items: readonly Item[], deleted: readonly Item[]): Candidate[] {
  const hit = (item: Item, inTrash: boolean): Candidate => ({
    kind: 'item',
    id: item.id,
    title: item.title,
    parent: inTrash ? 'Projects · Recently deleted' : `Projects · ${statusLabels[item.status]}`,
    url: `/projects?${inTrash ? 'view=deleted&' : ''}item=${encodeURIComponent(item.id)}`,
    fields: [item.title, item.description],
  })
  return [...items.map((item) => hit(item, false)), ...deleted.map((item) => hit(item, true))]
}

export function hitsForJobs(rows: readonly JobRow[]): Candidate[] {
  return rows.map((row) => ({
    kind: 'job',
    id: row.id,
    title: row.name,
    parent: `Jobs · ${row.dayText} · ${row.locationName}`,
    url: `/jobs/${encodeURIComponent(row.id)}`,
    fields: [
      row.name,
      row.id,
      row.locationName,
      row.clientName,
      ...row.cleaners.map((c) => c.name),
    ],
  }))
}

export function hitsForClients(
  clients: readonly {
    id: string
    name: string
    terms: string
    contactName: string
    /** Its people, their numbers and emails (current or old), its addresses. */
    words?: string
  }[],
  locations: readonly {
    id: string
    clientId: string
    name: string
    addressLine1: string
    postalCode: string | null
  }[],
): Candidate[] {
  const clientName = new Map(clients.map((c) => [c.id, c.name]))
  return [
    ...clients.map(
      (c): Candidate => ({
        kind: 'client',
        id: c.id,
        title: c.name,
        parent: `Clients · ${c.terms}`,
        url: `/clients/${encodeURIComponent(c.id)}`,
        fields: [c.name, c.id, c.contactName, c.words ?? ''],
      }),
    ),
    ...locations
      .filter((l) => clientName.has(l.clientId))
      .map(
        (l): Candidate => ({
          kind: 'location',
          id: l.id,
          title: l.name,
          parent: `${clientName.get(l.clientId) ?? 'Clients'} · ${l.addressLine1}`,
          url: `/clients/${encodeURIComponent(l.clientId)}?tab=location-${encodeURIComponent(l.id)}`,
          fields: [l.name, l.id, l.addressLine1, l.postalCode ?? ''],
        }),
      ),
  ]
}

export function hitsForCleaners(
  cleaners: readonly {
    id: string
    name: string
    shiftLabel: string
    worksAt: string
    phone: string
  }[],
): Candidate[] {
  return cleaners.map((c) => ({
    kind: 'cleaner',
    id: c.id,
    title: c.name,
    parent: `Cleaners · ${c.shiftLabel} · ${c.worksAt}`,
    url: `/cleaners/${encodeURIComponent(c.id)}`,
    fields: [c.name, c.id, c.phone, c.worksAt],
  }))
}

export function hitsForInvoices(
  invoices: readonly {
    id: string
    where: string
    what: string
    number: string
    clientName: string
    statusLabel: string
  }[],
): Candidate[] {
  return invoices.map((i) => ({
    kind: 'invoice',
    id: i.id,
    title: `${i.where} · ${i.what}`,
    parent: `Invoices · ${i.statusLabel} · ${i.number}`,
    url: `/invoices/${encodeURIComponent(i.id)}`,
    fields: [i.where, i.what, i.number, i.clientName, i.id],
  }))
}

export function hitsForOrders(
  orders: readonly { id: string; name: string; statusLabel: string; orderedOn: string }[],
): Candidate[] {
  return orders.map((o) => ({
    kind: 'order',
    id: o.id,
    title: `Order ${o.id} · ${o.name}`,
    parent: `Orders · ${o.statusLabel} · ${o.orderedOn}`,
    url: `/orders/${encodeURIComponent(o.id)}`,
    fields: [o.name, o.id],
  }))
}

export function hitsForUsers(users: readonly WorkspaceUser[]): Candidate[] {
  return users.map((user) => ({
    kind: user.status === 'invited' ? 'invitation' : 'user',
    id: user.id,
    title: user.name,
    parent: `Users · ${roleLabels[user.role]}${user.status === 'invited' ? ' · invited' : ''}`,
    url: `/settings/users?q=${encodeURIComponent(user.email)}`,
    // First and last name each match on their own, as well as together.
    fields: [user.name, user.firstName, user.lastName, user.email, roleLabels[user.role]],
  }))
}

/** The app's pages, from the feature registry — so a new screen is findable by itself. */
export function hitsForPages(
  features: readonly FeatureEntry[],
  showScaffold: boolean,
): Candidate[] {
  return features
    .filter((feature) => showScaffold || feature.status !== 'scaffold')
    .flatMap((feature) =>
      feature.routes.slice(0, 1).map(
        (route): Candidate => ({
          kind: 'page',
          id: `page:${route}`,
          title: feature.label,
          parent: route.startsWith('/settings/') ? 'Settings' : 'Pages',
          url: route,
          fields: [feature.label],
        }),
      ),
    )
}

/**
 * Rank candidates against the query, keep only what this role may open, and
 * return the best `limit`. Pages first on a tie, then records by title.
 */
export function rankHits(
  query: string,
  candidates: readonly Candidate[],
  role: Role,
  limit = 20,
): SearchHit[] {
  const kindOrder: SearchKind[] = ['page', 'item', 'user', 'invitation']
  return candidates
    .filter((hit) => {
      const access = accessForPath(hit.url.split('?')[0] ?? hit.url)
      return access !== 'public' && can(role, access)
    })
    .map((hit) => ({ hit, rank: score(query, hit.fields) }))
    .filter(({ rank }) => rank > 0)
    .sort(
      (a, b) =>
        b.rank - a.rank ||
        kindOrder.indexOf(a.hit.kind) - kindOrder.indexOf(b.hit.kind) ||
        a.hit.title.localeCompare(b.hit.title),
    )
    .slice(0, limit)
    .map(({ hit: { fields: _fields, ...hit } }) => hit)
}
